import {
  demandForActivityVia,
  slotsOverlappingWindow,
  timeToMinutes,
  SLOT_INTERVALS,
  type ActivityCoverage,
  type ActivityOverride,
  type ActivityQualification,
  type BoardActivity,
  type BoardDay,
  type CoverageStatus,
  type DayCoverage,
  type DayPresence,
} from './boardCoverage.js';
import { evaluateMetric, formatMetric, type MetricContext, type MetricRegistry } from './metricRegistry.js';
import type { PresenceSlot } from './domain.js';

// Activity-based presence (ADR-0011, Phase 1 — pure core, no DB/UI).
//
// The successor model: declared work is an ACTIVITY, not a time slot. One row
// per (activity, employee, date) carries a STATUS (declared vs assigned) and a
// SOURCE. Slots (matin/après-midi/soir) and the 3-square matrix are DERIVED
// from the union of a person's activity windows; « absent » is a day-level
// flag, not an activity. Coverage no longer filters by qualification — an
// activity is covered when it has ≥1 current-employee row (qualification is
// advisory, enforced at the write path per ADR §4).
//
// These functions intentionally return the SAME shapes as the slot-based
// computeDayCoverage / computeDayPresence, so the UI swaps its input source at
// cutover (Phase 4) without reshaping. The slot functions stay live until then.

/** ★ operator pin vs a soft declaration; 'assigned' supersedes 'declared' for the same cell. */
export type PresenceStatus = 'declared' | 'assigned';

/** Unified provenance across the former board_presence + board_assignments sources. */
export type ActivitySource = 'tap' | 'message' | 'desk' | 'clock' | 'operator';

/** One row of the unified board_activity_presence table. Provenance is optional so the pure coverage functions ignore it. */
export interface ActivityPresence {
  activityId: number;
  employeeId: string;
  date: string;
  status: PresenceStatus;
  source: ActivitySource;
  /** The acting employee (tap self / desk « via » / message author); null for clock/operator. */
  declaredBy?: string | null;
  messageId?: number | null;
}

/** A day-level « off » flag (board_absence, ADR §3). */
export interface BoardAbsence {
  employeeId: string;
  date: string;
  source: 'tap' | 'message' | 'desk';
  declaredBy?: string | null;
  messageId?: number | null;
}

/** A message-only slot signal awaiting arbitration (board_slot_intent, ADR §6). */
export interface SlotIntent {
  employeeId: string;
  date: string;
  slot: Exclude<PresenceSlot, 'absent'>;
  declaredBy?: string | null;
  messageId?: number | null;
}

/**
 * The ACTIVE activities an employee is QUALIFIED for whose DEFAULT window
 * overlaps a slot (ADR §6 message resolution — chat references the usual
 * schedule, so per-day overrides are deliberately ignored here). Drives « je
 * viens ce soir » → the single qualified evening activity (else a slot intent).
 */
export function qualifiedActivitiesInSlot(input: {
  slot: Exclude<PresenceSlot, 'absent'>;
  employeeId: string;
  activities: BoardActivity[];
  qualifications: ActivityQualification[];
}): BoardActivity[] {
  const [slotStart, slotEnd] = SLOT_INTERVALS[input.slot];
  const qualified = new Set(
    input.qualifications.filter((q) => q.employeeId === input.employeeId).map((q) => q.activityId),
  );
  return input.activities.filter((a) => {
    if (!a.active || !qualified.has(a.id)) return false;
    const ws = timeToMinutes(a.windowStart);
    const we = timeToMinutes(a.windowEnd);
    return ws < slotEnd && slotStart < we;
  });
}

/** Effective window for the day (override ⊕ default). */
/**
 * The effective window for a day: override ⊕ DERIVED ⊕ default.
 *
 * The derived layer is phase 3 of the predictive-staffing brief (§4.4): a
 * work-content activity's window slides with the day's load rather than being
 * configured wide enough for every scenario. A window wide enough for everything
 * is wrong in both directions — it inflates the capacity denominator (so head
 * count comes out low) and it lights up every slot it touches in the presence
 * matrix, which is the lossy slot proxy ADR-0011 exists to remove.
 *
 * Precedence is per BOUND, not per row, because an override may set only one of
 * the two. The operator's manual override always wins over the model (Hard Rule
 * 1 — the model proposes, the operator decides); the activity's configured
 * default is the floor when nothing else applies, so an activity with no
 * staffing profile behaves exactly as before.
 */
function windowOf(
  activity: BoardActivity,
  overridesByActivity: Map<number, ActivityOverride>,
  derived?: Map<number, { windowStart: string; windowEnd: string }>,
): { windowStart: string; windowEnd: string } {
  const override = overridesByActivity.get(activity.id);
  const computed = derived?.get(activity.id);
  return {
    windowStart: override?.windowStart ?? computed?.windowStart ?? activity.windowStart,
    windowEnd: override?.windowEnd ?? computed?.windowEnd ?? activity.windowEnd,
  };
}

/**
 * Coverage over activity rows (ADR-0011). For each ACTIVE activity in demand:
 * gather its current-employee rows; `assigned` = the ★ rows, `coveredBy` = the
 * declared rows (disjoint — one row per (activity, employee)). Covered when
 * either is non-empty; a waived override never gaps; below-threshold demand is
 * « pas de besoins ». `absentEmployeeIds` are dropped entirely. Qualification is
 * NOT a filter here (advisory only) — it is enforced when the row is written.
 */
export function computeActivityCoverage(input: {
  date: string;
  activities: BoardActivity[];
  overrides: ActivityOverride[];
  day: BoardDay | null;
  presences: ActivityPresence[];
  currentEmployeeIds: Iterable<string>;
  absentEmployeeIds?: Iterable<string>;
  /**
   * Windows the staffing model derived for this day (`derivedWindows`). Absent =
   * every activity keeps its configured window, i.e. today's behaviour — which
   * is what makes this parameter safe to add ahead of the profile rollout.
   */
  derivedWindows?: Map<number, { windowStart: string; windowEnd: string }>;
  /**
   * The metric registry and its per-day evaluation context (ADR-0015). Absent =
   * every activity gates on its legacy `demandFormula` and shows a bare number,
   * i.e. exactly today's behaviour — which is what lets this land before the
   * operator has configured a single metric.
   */
  metrics?: MetricRegistry;
  metricContext?: MetricContext;
}): DayCoverage {
  const current = new Set(input.currentEmployeeIds);

  // One memo for the whole day: an activity's load and badge often name the
  // same metric, and several activities may share one.
  const registry = input.metrics;
  const metricMemo = new Map<string, number | null>();
  const resolveMetric = (key: string): number | null => {
    if (!registry) return null;
    return evaluateMetric(registry, key, input.metricContext ?? { day: input.day }, metricMemo).value;
  };
  const badgeFor = (activity: BoardActivity): ActivityCoverage['badge'] => {
    if (!registry || !activity.badgeMetricKey) return null;
    const def = registry.get(activity.badgeMetricKey);
    if (!def) return null;
    const value = resolveMetric(activity.badgeMetricKey);
    const text = formatMetric(def, value);
    if (value === null || text === null) return null;
    return { key: def.key, labelFr: def.labelFr, text, value };
  };
  const absent = new Set(input.absentEmployeeIds ?? []);
  const overridesByActivity = new Map(
    input.overrides.filter((o) => o.date === input.date).map((o) => [o.activityId, o]),
  );

  // Rows for the day, per activity, split by status. Absent people never count.
  const assignedByActivity = new Map<number, string[]>();
  const declaredByActivity = new Map<number, string[]>();
  for (const p of input.presences) {
    if (p.date !== input.date || !current.has(p.employeeId) || absent.has(p.employeeId)) continue;
    const bucket = p.status === 'assigned' ? assignedByActivity : declaredByActivity;
    const list = bucket.get(p.activityId) ?? [];
    if (!list.includes(p.employeeId)) list.push(p.employeeId);
    bucket.set(p.activityId, list);
  }

  const activities = [...input.activities]
    .filter((a) => a.active)
    .sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label))
    .map((activity): ActivityCoverage => {
      const override = overridesByActivity.get(activity.id) ?? null;
      const { windowStart, windowEnd } = windowOf(activity, overridesByActivity, input.derivedWindows);
      const demand = demandForActivityVia(input.day, activity, resolveMetric);
      const assigned = assignedByActivity.get(activity.id) ?? [];
      const assignedSet = new Set(assigned);
      // A person assigned to an activity never also lists as its declared coverer.
      const coveredBy = (declaredByActivity.get(activity.id) ?? []).filter((id) => !assignedSet.has(id));

      const threshold = activity.demandDriver === 'always' ? 1 : (activity.minDemand ?? 1);
      const status: CoverageStatus =
        override && !override.required
          ? 'waived'
          : demand < threshold
            ? 'noDemand'
            : coveredBy.length > 0 || assigned.length > 0
              ? 'covered'
              : 'gap';

      return { activity, override, windowStart, windowEnd, demand, badge: badgeFor(activity), status, coveredBy, assigned };
    });

  return {
    date: input.date,
    day: input.day,
    demandEntered: input.day !== null,
    roomsToTurn: input.day?.departures ?? 0,
    gaps: activities.filter((a) => a.status === 'gap'),
    activities,
  };
}

/**
 * Per-employee 3-square presence (ADR-0011): the union of the thirds each of a
 * person's activity rows (declared OR assigned) overlaps. « absent » dominates
 * (a full-red cell, thirds cleared); anyone with no activity and no absence is
 * left OUT of the map (blank cell) — same contract as computeDayPresence.
 */
export function computeActivityPresence(input: {
  date: string;
  activities: BoardActivity[];
  overrides: ActivityOverride[];
  presences: ActivityPresence[];
  currentEmployeeIds: Iterable<string>;
  absentEmployeeIds?: Iterable<string>;
}): Map<string, DayPresence> {
  const current = new Set(input.currentEmployeeIds);
  const absent = new Set(input.absentEmployeeIds ?? []);
  const overridesByActivity = new Map(
    input.overrides.filter((o) => o.date === input.date).map((o) => [o.activityId, o]),
  );
  const activityById = new Map(input.activities.map((a) => [a.id, a]));
  const result = new Map<string, DayPresence>();
  const ensure = (id: string): DayPresence => {
    let p = result.get(id);
    if (!p) {
      p = { absent: false, matin: false, apresMidi: false, soir: false };
      result.set(id, p);
    }
    return p;
  };

  // Absence first — a declared-off day is a full-red cell regardless of any
  // stray activity row (which the write path should have cleared anyway).
  for (const id of absent) if (current.has(id)) ensure(id).absent = true;

  for (const pr of input.presences) {
    if (pr.date !== input.date || !current.has(pr.employeeId) || absent.has(pr.employeeId)) continue;
    const activity = activityById.get(pr.activityId);
    if (!activity) continue;
    const { windowStart, windowEnd } = windowOf(activity, overridesByActivity);
    const cell = ensure(pr.employeeId);
    for (const slot of slotsOverlappingWindow(windowStart, windowEnd)) cell[slot] = true;
  }

  // Drop empty entries (an activity with a zero-overlap window, no absence).
  for (const [id, p] of result) {
    if (!p.absent && !p.matin && !p.apresMidi && !p.soir) result.delete(id);
  }
  return result;
}
